<!-- SiteView.vue -->
<script lang="ts">
import { defineComponent, onMounted, computed, ref, type ComputedRef} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MapComponent from '@/components/MapComponent.vue'
import { useMapStore } from '@/stores/mapStore'
import NewSite from '@/components/NewSite.vue'
import VectorSource from 'ol/source/Vector.js'
import VectorLayer from 'ol/layer/Vector.js'
import Draw from 'ol/interaction/Draw'
import { createBox } from 'ol/interaction/Draw'
import { fromLonLat, toLonLat } from 'ol/proj'
import { useToast } from 'primevue/usetoast';


export default defineComponent({
  components: {
    MapComponent,
    NewSite
  },
  props: {
    redirectToNewSite: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    const siteId = computed(() => route.params.site_id) as ComputedRef<string>
    console.log("Site ID:", siteId.value)
    const mapStore = useMapStore()
    const drawSource = new VectorSource({ wrapX: false })
    const drawLayer = new VectorLayer({
      source: drawSource
    })
    const drawInteraction = new Draw({
      source: drawSource,
      type: 'Circle',
      geometryFunction: createBox()
    })
  
    const siteExtent = ref<number[]>([])
  
    // Size information
    const siteExtentMeters = ref<number[]>([])
    const siteArea = ref<number>(0)
    const toast = useToast();

    const startDrawing = () => {
      const map = mapStore.map
      if (!map) {
        throw new Error('Map not initialized')
      }
      map.addLayer(drawLayer)
      map.addInteraction(drawInteraction)

      drawInteraction.on('drawend', (event) => {
        const extent = event.feature.getGeometry()?.getExtent()
        // nullcheck
        if (!extent) {
          console.error('Extent is null')
          return
        }
        console.log('Extent:', extent)
        // convert the extent to lon/lat
        const [lonMin, latMin, lonMax, latMax] = extent
        const [lonMinLonLat, latMinLonLat] = toLonLat([lonMin, latMin])
        const [lonMaxLonLat, latMaxLonLat] = toLonLat([lonMax, latMax])
        console.log('Extent in lon/lat:', [lonMinLonLat, latMinLonLat, lonMaxLonLat, latMaxLonLat])

        // Set the extent in the NewSite component
        siteExtent.value = [latMinLonLat, latMaxLonLat, lonMinLonLat, lonMaxLonLat]
        // Use Hadamard to convert to distance
        const yDistanceLat = latMaxLonLat - latMinLonLat;
        const xDistanceLon = lonMaxLonLat - lonMinLonLat;
        const yDistanceMeters = yDistanceLat * Math.cos(latMinLonLat * (Math.PI / 180)) * 111320;
        const xDistanceMeters = xDistanceLon * 111320;
        siteExtentMeters.value = [yDistanceMeters / 1000, xDistanceMeters / 1000]
        siteArea.value = (yDistanceMeters * xDistanceMeters) / (1000 * 1000)

        // push a toast notification with the site dimensions
        if ( (siteArea.value) > 100 ) {
          toast.add({severity:'error', summary: 'Site drawn', detail: `This site is very large and may be deprioritised during processing. Area: ${siteArea.value.toFixed(2)} km² \n Areas less than 50 km² are prioritised. Height: ${yDistanceMeters.toFixed(2)} m, Width: ${xDistanceMeters.toFixed(2)} m.`, life: 7000});
        } else if (siteArea.value > 50) {
            toast.add({severity:'warn', summary: 'Site drawn', detail: `This site is relatively large and may be deprioritised during processing. Area: ${siteArea.value.toFixed(2)} km² \n Areas less than 50 km² are prioritised. Height: ${yDistanceMeters.toFixed(2)} m, Width: ${xDistanceMeters.toFixed(2)} m.`, life: 7000});
        } else {
          toast.add({severity:'success', summary: 'Site drawn', detail: `Site extent set. Area: ${siteArea.value.toFixed(2)} km² \n Height: ${yDistanceMeters.toFixed(2)} m, Width: ${xDistanceMeters.toFixed(2)} m.`, life: 7000});
        }
    
        drawSource.clear() // Clear any previous drawings
        map.removeInteraction(drawInteraction) // Remove interaction after drawing
      })
    }

    const siteSubmitted = (siteName) => {
      console.log('Site submitted, in SiteEditView.')
      console.log('Site name:', siteName)
      console.log('Redirect to new site:', props.redirectToNewSite)
      if (props.redirectToNewSite) {
        router.push(`/site/${siteName}`)
      } else {
        emit('site-submitted', siteName)
      }
    }

    // const stopDrawing = () => {
    //   const map = mapStore.map
    //   if (!map) {
    //     throw new Error('Map not initialized')
    //   }
    //   map.removeLayer(drawLayer)
    //   map.removeInteraction(drawInteraction)
    // }

    onMounted(() => {
      if (!mapStore.map) {
        throw new Error('Map not initialized')
      }
    })

    return {
      siteId,
      startDrawing,
      // stopDrawing,
      siteSubmitted,
      siteExtent,
      siteExtentMeters,
      siteArea
    }
  }
})
</script>

<template>
  <!-- Two column divs flex -->
  <!-- <h1>New site definition</h1> -->
  <div class="flex">
    <div class="w-1over3 gap-4">
      <!-- Add your input fields here -->
      <!-- Pass parameters to the component like site_id -->
      <NewSite :site_id="siteId" :site_extent="siteExtent" @start-drawing="startDrawing" @site-submitted="siteSubmitted" />
    </div>
    <div class="w-2over3">
      <!-- <Button @click="startDrawing">Draw Site Extent</Button> -->
      <MapComponent />
    </div>
  </div>
  <Toast />
</template>

<style scoped>
.flex {
  display: flex;
}
.w-1over3 {
  /* width: 33.33%; */
  flex-grow: 1;
}
.w-2over3 {
  /* width: 66.67%; */
  flex-grow: 2;
}

</style>